import * as React from 'react';
import { FC, useState, useCallback, useMemo } from 'react';
import { ListGroupItem, ListGroup, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import { SettingsItemContentProps } from '../SettingsItem';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { IconButton } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import ProfileConfigurationModal from './ProfileConfigurationModal';
import { PaymentsSettingsData, PaymentProfile, PaymentProfileType } from '../../../models';
import { Guid } from '../../../shared/utils';
import AppButton from '../../../components/AppButton';

export interface PaymentsSettingsProps extends SettingsItemContentProps {
    values: PaymentsSettingsData,
    onChange: (settings: PaymentsSettingsData, save?: boolean) => void,
}

const generateEmptyPaymentProfile = (): PaymentProfile => ({
    id: Guid.newGuid(),
    creationDate: new Date().toISOString(),
    name: '',
    type: PaymentProfileType.Absolute,
    version: 1,
    table: [],
})

const PaymentProfilesSettings : FC<PaymentsSettingsProps> = ({
    id,
    values,
    onChange,
}) => {
    const t = useTranslations();

    const payments = useMemo(
        () => values.profiles
            .filter(r => !values.profiles.some(o => o.id === r.id && o.version > r.version)), 
        [values.profiles]);

    const [editingPayment, setEditingPayment] = useState<PaymentProfile|undefined>(undefined);
    const [deletingId, setDeletingId] = React.useState<string|undefined>(undefined);
    
    const closeDeleteModal = React.useCallback(() => setDeletingId(undefined), [deletingId]);
    const onAddNew = useCallback(() => setEditingPayment(generateEmptyPaymentProfile()), [setEditingPayment]);
    const onSelect = useCallback((payment: PaymentProfile) => () => setEditingPayment({...payment, version: payment.version+1}), [setEditingPayment]);
    const onClosePayment = useCallback(() => setEditingPayment(undefined), [setEditingPayment]);
    

    const onSavePayment = useCallback((newPayment?: PaymentProfile) => {
        onChange({
            ...values,
            profiles: !newPayment ? payments.filter(r => r.id !== deletingId) : [...payments, { ...newPayment }]
        }, true);
        setDeletingId(undefined);
        setEditingPayment(undefined);
    }, [editingPayment, payments, deletingId, values, onChange]);

    const paymentsOrder = useMemo<Record<string, number>>(
        () => !payments?.length ? {} : Object.fromEntries(payments.map(p => [p.id, payments.filter(pp => pp.name.toLocaleLowerCase() < p.name.toLocaleLowerCase()).length])), 
        [payments])

    return (
        <>
            <Alert color="secondary" isOpen>
                {t(TK.paymentProfilesSettingsDescription)}
            </Alert>

            <ListGroup>
                { payments.map((payment) => (
                    <ListGroupItem key={payment.id+payment.version} style={{order: paymentsOrder[payment.id] ?? 0}}>
                        <AppButton id={`${id}-open-profile-${payment.id}-button`} onClick={onSelect(payment)} color="link">{payment.name}</AppButton>
                    </ListGroupItem>
                ))}
                <ListGroupItem style={{order: 1000000}}>
                    <IconButton id={`${id}-add-button`} onClick={onAddNew} size="small"><AddCircleIcon/>&nbsp;{t(TK.new)}</IconButton>
                </ListGroupItem>
            </ListGroup>

            <ProfileConfigurationModal 
                defaultValue={editingPayment}
                onSave={onSavePayment}
                onClose={onClosePayment}
                onDelete={setDeletingId}
            />
            
            <Modal isOpen={!!deletingId} toggle={closeDeleteModal}>
                <ModalHeader toggle={closeDeleteModal}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>{t(TK.youRAboutToDelete)}</ModalBody>
                <ModalFooter>
                    <AppButton id={`${id}-confirm-delete-button`} color="danger" onClick={() => onSavePayment()}>{t(TK.delete)}</AppButton>{' '}
                    <AppButton id={`${id}-cancel-delete-button`} color="secondary" onClick={closeDeleteModal}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default PaymentProfilesSettings;